/**
 * FiscalAPI.js
 *
 * Funções de API para o módulo de Nota Fiscal (NFS-e).
 * Separado do API.js principal para manter o arquivo principal gerenciável.
 */

import { getAccessToken } from './authTokens';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://www.gabriela.app.br/api/';

// Endpoints fiscais
const FISCAL_SETTINGS_GET          = BASE_URL + 'fiscal/get_fiscal_settings.php';
const FISCAL_SETTINGS_SET          = BASE_URL + 'fiscal/set_fiscal_settings.php';
const FISCAL_INVOICE_GET           = BASE_URL + 'fiscal/get_fiscal_invoice.php';
const FISCAL_INVOICE_HISTORY       = BASE_URL + 'fiscal/get_fiscal_invoice_history.php';
const FISCAL_INVOICE_BY_PATIENT    = BASE_URL + 'fiscal/get_fiscal_invoices_by_patient.php';
const FISCAL_INVOICE_CREATE        = BASE_URL + 'fiscal/create_fiscal_invoice.php';
const FISCAL_INVOICE_REFRESH       = BASE_URL + 'fiscal/refresh_fiscal_invoice_status.php';
const FISCAL_INVOICE_SEND_WHATSAPP = BASE_URL + 'fiscal/send_fiscal_invoice_whatsapp.php';
const FISCAL_INVOICE_DOWNLOAD      = BASE_URL + 'fiscal/download_fiscal_file.php';
const FISCAL_INVOICE_CANCEL        = BASE_URL + 'fiscal/cancel_fiscal_invoice.php';

// ─── Helpers internos ────────────────────────────────────────────────────────

const getToken = () => getAccessToken();

const callGet = (url, callback) => {
    fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
        },
    })
        .then(res => res.json())
        .then(data => callback(data))
        .catch(err => callback({ success: false, error: err.message || 'Erro de conexão' }));
};

const callPostJson = (url, body, callback) => {
    fetch(url, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    })
        .then(res => res.json())
        .then(data => callback(data))
        .catch(err => callback({ success: false, error: err.message || 'Erro de conexão' }));
};

// ─── API pública ─────────────────────────────────────────────────────────────

/**
 * Retorna as configurações fiscais da empresa.
 * @param {Function} callback
 */
export const GetFiscalSettingsAPI = (callback) => {
    callGet(FISCAL_SETTINGS_GET, callback);
};

/**
 * Cria ou atualiza as configurações fiscais da empresa.
 * @param {Object} settings - Campos de configuração fiscal
 * @param {Function} callback
 */
export const SetFiscalSettingsAPI = (settings, callback) => {
    callPostJson(FISCAL_SETTINGS_SET, settings, callback);
};

/**
 * Retorna os dados de uma nota fiscal.
 * @param {{ appointmentId?: number, invoiceId?: number }} params
 * @param {Function} callback
 */
export const GetFiscalInvoiceAPI = (params, callback) => {
    const query = params.invoiceId
        ? `?invoiceId=${params.invoiceId}`
        : `?appointmentId=${params.appointmentId}`;
    callGet(FISCAL_INVOICE_GET + query, callback);
};

/**
 * Cria uma solicitação de emissão de nota fiscal.
 * @param {{
 *   appointmentId: number,
 *   paymentId: number,
 *   serviceDescription: string,
 *   sendEmail: boolean,
 *   tomador?: { name: string, document: string, email?: string },
 *   amount?: number,
 *   serviceCode?: string
 * }} data
 * @param {Function} callback
 */
export const CreateFiscalInvoiceAPI = (data, callback) => {
    // Monta o payload incluindo os overrides opcionais do modal de revisão
    const payload = {
        appointmentId: data.appointmentId,
        paymentId: data.paymentId,
        serviceDescription: data.serviceDescription,
        sendEmail: data.sendEmail,
    };

    // Overrides opcionais enviados pelo modal de revisão
    if (data.tomador) {
        payload.tomador = data.tomador;
    }
    if (data.amount !== undefined && data.amount !== null) {
        payload.amount = data.amount;
    }
    if (data.serviceCode !== undefined && data.serviceCode !== null && data.serviceCode !== '') {
        payload.serviceCode = data.serviceCode;
    }

    callPostJson(FISCAL_INVOICE_CREATE, payload, callback);
};

/**
 * Atualiza o status de uma nota fiscal consultando o provider.
 * @param {number} invoiceId
 * @param {Function} callback
 */
export const RefreshFiscalInvoiceStatusAPI = (invoiceId, callback) => {
    callPostJson(FISCAL_INVOICE_REFRESH, { invoiceId }, callback);
};

/**
 * Envia a nota ao paciente pelo número próprio de WhatsApp da clínica, na hora,
 * com o link do PDF em botão. Sem número próprio o frontend abre o WhatsApp Web.
 * @param {number} invoiceId
 * @param {function} callback - { success, status: 'sent'|'queued' } ou { success: false, reason, error }
 */
export const SendFiscalInvoiceWhatsAppAPI = (invoiceId, callback) => {
    callPostJson(FISCAL_INVOICE_SEND_WHATSAPP, { invoiceId }, callback);
};

/**
 * Retorna a URL autenticada para download do PDF ou XML de uma nota fiscal.
 * O download deve ser feito via window.open ou link com o token no header.
 * @param {number} invoiceId
 * @param {'pdf'|'xml'} type
 * @returns {string} URL de download
 */
export const GetFiscalInvoiceDownloadUrl = (invoiceId, type) => {
    return `${FISCAL_INVOICE_DOWNLOAD}?invoiceId=${invoiceId}&type=${type}`;
};

/**
 * Sanitiza uma string para uso seguro em nome de arquivo.
 * Remove acentos, substitui espaços por underscore e remove caracteres especiais.
 * Replica a lógica da função sanitizeFilename() do PHP.
 * @param {string} str
 * @returns {string}
 */
export const sanitizeFiscalFilename = (str) => {
    if (!str) return '';
    // Normaliza unicode e remove diacríticos (acentos)
    const normalized = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Substitui espaços por underscore
    const underscored = normalized.replace(/\s+/g, '_');
    // Remove qualquer caractere que não seja letra, número, underscore ou hífen
    const clean = underscored.replace(/[^A-Za-z0-9_\-]/g, '');
    // Remove underscores/hífens duplicados e trim
    return clean.replace(/[_\-]{2,}/g, '_').replace(/^[_\-]+|[_\-]+$/g, '');
};

/**
 * Constrói o nome amigável do arquivo fiscal no formato:
 * {YYYY-MM-DD}-{Nome_Cliente}-nfse_{numero}.{ext}
 * Ex: 2026-05-04-Luiz_Carlos_Fortino_Rebelo-nfse_3094.pdf
 *
 * @param {{ issued_at?: string, patient_name?: string, invoice_number?: number|string, id?: number }} invoice
 * @param {'pdf'|'xml'} type
 * @returns {string}
 */
export const buildFiscalFilename = (invoice, type) => {
    const issuedDate  = invoice?.issued_at
        ? invoice.issued_at.substring(0, 10)
        : new Date().toISOString().substring(0, 10);
    const patientSlug = sanitizeFiscalFilename(invoice?.patient_name) || 'paciente';
    const invoiceNum  = invoice?.invoice_number ?? invoice?.id ?? 'nfse';
    return `${issuedDate}-${patientSlug}-nfse_${invoiceNum}.${type}`;
};

/**
 * Faz o download de um arquivo fiscal (PDF ou XML) e abre em nova aba.
 * Usa fetch com Authorization header para endpoints protegidos.
 * O nome do arquivo é construído a partir dos dados da nota (invoice).
 * @param {number} invoiceId
 * @param {'pdf'|'xml'} type
 * @param {{ issued_at?: string, patient_name?: string, invoice_number?: number|string }} [invoice]
 */
export const DownloadFiscalFileAPI = (invoiceId, type, invoice = null) => {
    const url      = GetFiscalInvoiceDownloadUrl(invoiceId, type);
    const filename = invoice
        ? buildFiscalFilename(invoice, type)
        : `nota_fiscal_${invoiceId}.${type}`;
    fetch(url, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${getToken()}`,
        },
    })
        .then(res => {
            if (!res.ok) throw new Error('Arquivo não disponível');
            return res.blob();
        })
        .then(blob => {
            const blobUrl  = window.URL.createObjectURL(blob);
            const link     = document.createElement('a');
            link.href      = blobUrl;
            link.download  = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => {});
};

/**
 * Cancela uma NFS-e emitida.
 * @param {{ invoiceId: number, justificativa: string }} data
 * @param {Function} callback
 */
export const CancelFiscalInvoiceAPI = (data, callback) => {
    callPostJson(FISCAL_INVOICE_CANCEL, data, callback);
};

/**
 * Retorna o histórico completo de notas fiscais de um atendimento.
 * Inclui notas canceladas, com erro e a nota ativa (se houver).
 * @param {number} appointmentId
 * @param {number} companyId
 * @param {Function} callback
 */
export const GetFiscalInvoiceHistoryAPI = (appointmentId, companyId, callback) => {
    callGet(`${FISCAL_INVOICE_HISTORY}?appointment_id=${appointmentId}&company_id=${companyId}`, callback);
};

/**
 * Busca todas as notas fiscais emitidas para um paciente.
 * @param {number} patientId
 * @param {function} callback - (result) => void
 */
export const GetFiscalInvoicesByPatientAPI = (patientId, callback) => {
    callGet(`${FISCAL_INVOICE_BY_PATIENT}?patient_id=${patientId}`, callback);
};

/**
 * Gera o link público para visualização do PDF da nota fiscal no browser.
 *
 * Usa um único parâmetro opaco `key` (64 chars hex) que contém o ID mascarado
 * e o HMAC de validação — sem expor o ID sequencial na URL.
 *
 * O `public_key` é calculado pelo backend e retornado em get_fiscal_invoice_history.php.
 * O frontend apenas monta a URL com esse valor — nunca calcula o HMAC localmente.
 *
 * @param {string} publicKey - Chave opaca retornada pelo backend (invoice.public_key)
 * @returns {string|null} URL pública para visualização do PDF, ou null se parâmetro inválido
 */
export const GetFiscalPublicPdfUrl = (publicKey) => {
    if (!publicKey) return null;
    return `${BASE_URL}public/nf/${publicKey}`;
};

/**
 * Texto curto da situação do último envio da nota por WhatsApp pelo sistema,
 * automático ou pelo botão "Enviar" (campo auto_send devolvido por
 * get_fiscal_invoice_history.php).
 *
 * @param {{status: string, at: string, error: ?string}|null} autoSend
 * @returns {string|null}
 */
export const describeFiscalAutoSend = (autoSend) => {
    if (!autoSend?.status) return null;
    const at = (autoSend.at || '').slice(0, 16); // 'YYYY-MM-DD HH:MM'
    const when = at
        ? ` em ${at.slice(8, 10)}/${at.slice(5, 7)}/${at.slice(0, 4)} às ${at.slice(11, 16)}`
        : '';
    switch (autoSend.status) {
        case 'sent':     return `Enviada ao paciente por WhatsApp${when}`;
        case 'queued':   return 'Envio por WhatsApp na fila...';
        case 'failed':   return `Envio por WhatsApp falhou${autoSend.error ? `: ${autoSend.error}` : ''}`;
        case 'canceled': return 'Envio por WhatsApp cancelado';
        default:         return null;
    }
};
